/**
 * The reading pane's header: what this letter is, who wrote it, and what can be
 * done about it.
 *
 * Two rows, each with one job. The top row is the subject with every action on
 * its right; the bottom row is the sender, with the records this conversation
 * was matched to pushed against the far end. What used to be a third row - a
 * strip of labelled pills mixing a search field, two links and two buttons - is
 * gone: every control on it moved into the circles above, and every fact on it
 * became a chip on the sender line.
 */
import { cn } from "@wa/ui";
import type { MailThread } from "../../../../lib/mail";
import type { CrmLabel } from "../../../../lib/crmLabels";
import { useT } from "../../../../i18n/I18nProvider";
import { Avatar, LabelChips, fmtDateTime } from "../../../ui";
import { PaneBack } from "../../_shared/workMobile";
import { useWorkCompact } from "../../_shared/density";
import type { TemplateSeed } from "../TemplateMenu";
import { ThreadActions } from "./ThreadActions";
import { ThreadTags } from "./ThreadTags";

export function ThreadHead({
  thread,
  labels,
  campaignName,
  onReply,
  onForward,
  onPatch,
  onDelete,
  onChanged,
  onBack,
}: {
  thread: MailThread;
  labels: CrmLabel[];
  campaignName?: string;
  /** Back to the list. Only reachable on a phone, which shows one pane. */
  onBack: () => void;
  onReply: (seed?: TemplateSeed) => void;
  onForward: () => void;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const compact = useWorkCompact();
  const messages = thread.messages ?? [];
  /**
   * Who this line is about: the first person who wrote TO us, falling back to
   * the newest message on a thread we started and nobody has answered.
   *
   * It used to read the matched company name here, which put a company in bold
   * above an address belonging to a person, and then repeated that company as a
   * chip a centimetre below. The name and the address now come from the same
   * message, so they cannot disagree, and the company keeps its own chip on the
   * right of this row.
   */
  const sender = messages.find((m) => m.direction === "inbound") ?? messages[messages.length - 1];
  const who = sender?.fromName ?? sender?.fromAddress ?? thread.client?.name ?? "-";

  return (
    <div className="crm-read-head">
      <div className={cn("crm-read-top flex items-start", compact ? "gap-2.5" : "gap-3.5")}>
        <PaneBack label={t("common.back")} onBack={onBack} />
        <div className="crm-read-title">
          <div className="crm-read-subject">{thread.subject || "-"}</div>
          <LabelChips labels={labels} tags={thread.tags} className={compact ? "mt-1.5" : "mt-2"} />
        </div>

        <ThreadActions
          thread={thread}
          labels={labels}
          onReply={onReply}
          onForward={onForward}
          onPatch={onPatch}
          onDelete={onDelete}
          onChanged={onChanged}
        />
      </div>

      <div className="crm-read-meta">
        <Avatar name={who} size={compact ? 30 : 40} />
        <span className="min-w-0 flex-1">
          {/* Truncated, because `who` falls through to a bare address when the
              sender has no name, and an address is one unbreakable word that
              ran straight under the chips beside it. */}
          <span className={cn("crm-trunc block font-[650]", compact ? "text-[12.75px]" : "text-[13.5px]")}>
            {who}
          </span>
          <span className={cn("crm-mut crm-trunc block", compact ? "text-[11px]" : "mt-0.5 text-[11.75px]")}>
            {sender?.fromAddress ?? "-"} &middot; {fmtDateTime(thread.lastMessageAt ?? thread.createdAt)}
          </span>
        </span>

        <ThreadTags thread={thread} campaignName={campaignName} />
      </div>
    </div>
  );
}
